import { useState } from 'react';

import { DEFAULT_ENTRIES_PER_PAGE } from '../../constants';
import { StorageEntity } from '../../utils/storages/storageEntity';
import { BaseTableFilterStorage, BaseTableFilterStorageKey, SortBy, UseBaseTableFilters } from './types';
import usePagination from './usePagination';

type Props<Sort = string> = {
  initialSorting?: SortBy<Sort>[];
  storage?: StorageEntity<BaseTableFilterStorage<Sort>>;
};
const useBaseTableFilters = <Sort = string>({
  initialSorting,
  storage,
}: Props<Sort> = {}): UseBaseTableFilters<Sort> => {
  const { paginationOptions, setCurrentPage, handleEntriesPerPage, handleChangePage } = usePagination({ storage });
  const [searchValue, setSearchValue] = useState(() => {
    return storage?.getStorageData(BaseTableFilterStorageKey.searchValue) || '';
  });

  const [sortBy, setSortBy] = useState<SortBy<Sort>[]>(() => {
    const storageSortBy = storage?.getStorageData(BaseTableFilterStorageKey.sortBy);
    if (storageSortBy) {
      return storageSortBy;
    }
    return initialSorting || [];
  });

  const handleSearch = (newValue: string): void => {
    const trimedValue = newValue ? newValue.trim() : newValue;
    setSearchValue(trimedValue);
    if (storage) {
      storage.setStorageData(BaseTableFilterStorageKey.searchValue, newValue);
    }
    setCurrentPage(0);
  };

  const handleSort = (columnId: Sort, isSorted: boolean, isSortedDesc?: boolean): void => {
    const sortBy = isSorted && isSortedDesc ? [] : [{ id: columnId, desc: isSortedDesc !== undefined }];
    setSortBy(sortBy);
    if (storage) {
      storage.setStorageData(BaseTableFilterStorageKey.sortBy, sortBy);
    }
    setCurrentPage(0);
  };

  const resetFilters = (): void => {
    setCurrentPage(0);
    const newSorting = initialSorting || [];
    setSortBy(newSorting);
    handleEntriesPerPage(DEFAULT_ENTRIES_PER_PAGE.toString());
    setSearchValue('');
    if (storage) {
      storage.setStorageData(BaseTableFilterStorageKey.searchValue, '');
      storage.setStorageData(BaseTableFilterStorageKey.sortBy, newSorting);
    }
  };

  return {
    paginationOptions,
    sortBy,
    searchValue,
    handleEntriesPerPage,
    handleChangePage,
    handleSearch,
    handleSort,
    setCurrentPage,
    resetFilters,
  };
};

export default useBaseTableFilters;
